import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  useMediaQuery,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Skeleton,
} from "@mui/material";
import { CatalogPagination } from "../components/CatalogPagination";
import { Search, Share, ContentCopy, ArrowBack, Close, Bookmark, BookmarkBorder } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams, useParams, Link as RouterLink } from "react-router-dom";
import { ThumbnailImage } from "../components/ThumbnailImage";
import { formatCreationDateThumbnail } from "../utils/catalogFormatters";
import { SortSelector } from "../components/SortSelector";
import { loadSortPreference, saveSortPreference, SortOption, toSearchSortBy } from "../utils/sortUtils";
import { useBrowse } from "../hooks/useBrowse";
import { useBookmarks } from "../hooks/useBookmarks";
import { fetchUserPublicCatalog } from "../utils/visibilityApi";
import { featureFlags } from "../utils/featureFlags";
import { useCatalogFacetFilters, ALL_CATALOG_FACETS } from "../hooks/useCatalogFacetFilters";
import { CatalogFilterRail } from "../components/catalog/CatalogFilterRail";

const ITEMS_PER_PAGE = 12;
const SEARCH_DEBOUNCE_MS = 300;

export const PublicCatalog = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { aliId } = useParams<{ aliId?: string }>();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));

  const [localSearch, setLocalSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const showInfoBar = searchParams.get('from') === 'upload';
  const { isBookmarked, toggleBookmark } = useBookmarks();

  // GH#1263: server-side facet filter rail, behind enableCatalogFilters.
  // Off in prod until validated on test — flag OFF must preserve today's
  // behavior exactly (no rail, no facet params sent to useBrowse).
  const catalogFiltersEnabled = featureFlags.isEnabled('enableCatalogFilters');
  // Selections clear whenever the catalog scope changes (aliId route param);
  // '__global__' is a stable scope for the un-scoped /public/catalog route.
  const facetScopeKey = aliId ?? '__global__';
  const {
    selected: selectedFacets,
    toggle: toggleFacet,
    clearAll: clearAllFacets,
    filtersActive,
    browseParams: facetBrowseParams,
  } = useCatalogFacetFilters(facetScopeKey);

  // Per-user catalog owner info (only set when aliId is in URL)
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | null>(null);
  const [ownerAliId, setOwnerAliId] = useState<string | null>(null);
  const [hasPublicCV, setHasPublicCV] = useState<boolean>(false);
  const [ownerLoading, setOwnerLoading] = useState(!!aliId);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Sort state with localStorage persistence
  const [sortOption, setSortOption] = useState<SortOption>(() => loadSortPreference());

  const handleSortChange = useCallback((option: SortOption) => {
    setSortOption(option);
    saveSortPreference(option);
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(localSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [localSearch]);

  // Server-side browse via search API. Facet params are only sent when the
  // rail is enabled — flag OFF must produce byte-identical requests to
  // before this MR (no workType/materials/.../facets params at all).
  const { items, total, page, setPage, loading, error, totalPages, facetBuckets } = useBrowse({
    sortBy: toSearchSortBy(sortOption),
    pageSize: ITEMS_PER_PAGE,
    q: debouncedSearch || undefined,
    ownerAliId: aliId,
    ...(catalogFiltersEnabled
      ? {
          workType: facetBrowseParams.workType,
          materials: facetBrowseParams.materials,
          techniques: facetBrowseParams.techniques,
          creators: facetBrowseParams.creators,
          decade: facetBrowseParams.decade,
          facets: ALL_CATALOG_FACETS,
        }
      : {}),
  });

  // Fetch owner display name for per-user catalogs
  useEffect(() => {
    if (!aliId) {
      setOwnerDisplayName(null);
      setOwnerAliId(null);
      setOwnerLoading(false);
      return;
    }

    let cancelled = false;
    const loadOwnerInfo = async () => {
      try {
        setOwnerLoading(true);
        const response = await fetchUserPublicCatalog(aliId);
        if (!cancelled) {
          setOwnerDisplayName(response.ownerDisplayName);
          setOwnerAliId(response.ownerAliId);
          setHasPublicCV(response.hasPublicCV ?? false);
        }
      } catch {
        // Owner info is supplementary — don't block the page
        if (!cancelled) {
          setOwnerDisplayName(null);
          setOwnerAliId(aliId.toUpperCase());
          setHasPublicCV(false);
        }
      } finally {
        if (!cancelled) setOwnerLoading(false);
      }
    };

    loadOwnerInfo();
    return () => { cancelled = true; };
  }, [aliId]);

  // Whether to show the filtered-empty state (distinct from the genuinely
  // empty catalog): only meaningful when the rail is enabled and active.
  const showFilteredEmpty = catalogFiltersEnabled && filtersActive && items.length === 0;

  const shareUrl = window.location.href;

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage('Link copied to clipboard!');
    setSnackbarOpen(true);
    setShareModalOpen(false);
  };

  const getGridTemplate = () => {
    if (isMobile)
      return {
        columns: "repeat(2, 1fr)",
        rows: "repeat(6, 1fr)",
      };
    if (isTablet)
      return {
        columns: "repeat(3, 1fr)",
        rows: "repeat(4, 1fr)",
      };
    if (isLaptop)
      return {
        columns: "repeat(4, 1fr)",
        rows: "repeat(3, 1fr)",
      };
    return {
      columns: "repeat(6, 1fr)",
      rows: "repeat(2, 1fr)",
    };
  };

  const gridTemplate = getGridTemplate();
  const isShowingGrid = items.length > 0 && !loading && !error;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Info Bar - shown when coming from upload page */}
      {showInfoBar && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/my-objects')}
            >
              Cancel
            </Button>
          }
        >
          This content about your object is: <strong>Public</strong>
        </Alert>
      )}

      {/* Title */}
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1, sm: 0 },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(aliId ? '/public/catalog' : '/')} aria-label="Back to catalog" sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            {ownerLoading && aliId ? (
              <>
                <Skeleton variant="text" width={300} sx={{ fontSize: "2rem" }} />
                <Skeleton variant="text" width={100} sx={{ mb: 2 }} />
              </>
            ) : (
              <>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    color: theme.palette.primary.main,
                    fontFamily: "Libre Baskerville, serif",
                    fontWeight: "bold",
                    mb: 0.5,
                  }}
                >
                  {ownerDisplayName && ownerAliId && hasPublicCV ? (
                    <RouterLink
                      to={`/cv/public/${ownerAliId}`}
                      aria-label={`View ${ownerDisplayName}'s public CV`}
                      style={{
                        color: "inherit",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "none"; }}
                    >
                      {ownerDisplayName}
                    </RouterLink>
                  ) : ownerDisplayName ? (
                    ownerDisplayName
                  ) : (
                    'Public Catalog'
                  )}
                </Typography>
                {ownerDisplayName && (
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 2, fontFamily: "Libre Baskerville, serif" }}
                  >
                    {ownerAliId ? `Public Catalog · ${ownerAliId}` : 'Public Catalog'}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Share />} onClick={handleShare}>
            Share
          </Button>
        </Box>
      </Box>

      {/* Filter rail (flag-gated) + results column */}
      <Box
        sx={
          catalogFiltersEnabled
            ? {
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '288px 1fr' },
                gap: 3,
                alignItems: 'start',
              }
            : undefined
        }
      >
        {catalogFiltersEnabled && (
          <CatalogFilterRail
            buckets={facetBuckets}
            selected={selectedFacets}
            onToggle={toggleFacet}
            onClearAll={clearAllFacets}
          />
        )}

        <Box sx={{ minWidth: 0 }}>
          {/* Search Bar */}
          <Box
            sx={{
              mb: 4,
              maxWidth: "100%",
              display: "flex",
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: { xs: 1, sm: 2 },
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Search titles, keywords, creators, materials..."
              size="small"
              fullWidth
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <SortSelector value={sortOption} onChange={handleSortChange} />
          </Box>

          {/* Objects Grid */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Objects ({total})
          </Typography>

          {/* Grid */}
          <Box
            sx={{
              bgcolor: isShowingGrid ? theme.palette.divider : "background.paper",
              border: `1px solid ${theme.palette.divider}`,
              minHeight: { xs: 400, sm: 600, md: 500, lg: 400 },
              display: isShowingGrid ? "grid" : "flex",
              gridTemplateColumns: isShowingGrid ? gridTemplate.columns : undefined,
              gridAutoRows: "minmax(200px, auto)",
              gap: isShowingGrid ? "1px" : 0,
              alignItems: isShowingGrid ? "stretch" : "center",
              justifyContent: "center",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading public catalog...
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            ) : showFilteredEmpty ? (
              <Box sx={{ textAlign: "center", p: 4 }} data-testid="catalog-filtered-empty">
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  No objects match these filters
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Try clearing a filter to see more objects.
                </Typography>
                <Button variant="outlined" onClick={clearAllFacets}>
                  Clear filters
                </Button>
              </Box>
            ) : items.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 4 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  No objects yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {aliId
                    ? 'This creator hasn\u2019t shared any objects publicly yet. Check back later!'
                    : 'Creators haven\u2019t shared any objects publicly yet. Check back later!'}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Render browse items */}
                {items.map((item, index) => {
                  const title = item.title || 'Untitled';
                  const bookmarked = isBookmarked(item.catalogItemId);

                  return (
                    <Box
                      key={item.catalogItemId}
                      onClick={() =>
                        navigate(
                          `/public/object-details/${item.catalogItemId}${showInfoBar ? '?from=upload' : ''}`,
                          {
                            state: {
                              itemIds: items.map(i => i.catalogItemId),
                              currentIndex: index,
                              routePrefix: '/public/object-details',
                            },
                          },
                        )
                      }
                      sx={{
                        p: { xs: 1, sm: 1.5, md: 2 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        overflow: "hidden",
                        cursor: "pointer",
                        position: "relative",
                        bgcolor: "background.paper",
                        "&:hover": {
                          bgcolor: theme.palette.action.hover,
                        },
                        "&:hover .bookmark-btn, &:focus-within .bookmark-btn": {
                          opacity: 1,
                        },
                        "@media (hover: none)": {
                          ".bookmark-btn": { opacity: 1 },
                        },
                      }}
                    >
                      <IconButton
                        className="bookmark-btn"
                        size="small"
                        aria-label={`Bookmark ${title}`}
                        aria-pressed={bookmarked}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBookmark(item.catalogItemId);
                        }}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                          color: bookmarked ? "primary.main" : "text.secondary",
                          opacity: bookmarked ? 1 : 0,
                          transition: "opacity 150ms ease-in-out",
                          zIndex: 2,
                        }}
                      >
                        {bookmarked ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
                      </IconButton>
                      <Box sx={{
                        position: "relative",
                        mb: { xs: 1, sm: 1.5, md: 2 },
                        height: { xs: 120, sm: 150, md: 180, lg: 200 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                        <ThumbnailImage
                          src={item.coverImageUrl || undefined}
                          alt={title}
                        />
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontStyle: "italic",
                          mb: 0.5,
                          fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mb: 0.5,
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        {item.creationDate || (item.creationYear ? String(item.creationYear) : '')}
                      </Typography>
                      {item.aliCatalogNumber && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.7rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {item.aliCatalogNumber}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {/* Fill remaining grid cells with empty boxes if needed */}
                {Array.from({
                  length: Math.max(0, ITEMS_PER_PAGE - items.length),
                }).map((_, index) => (
                  <Box key={`empty-${index}`} sx={{ p: 2, bgcolor: "background.paper" }} />
                ))}
              </>
            )}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <CatalogPagination
                count={totalPages}
                page={page + 1}
                onChange={(_event, p) => setPage(p - 1)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Share Public Catalog
          <IconButton onClick={() => setShareModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Share Link"
            value={shareUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyLink}>
                  <ContentCopy />
                </IconButton>
              ),
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default PublicCatalog;
